// src/components/admin/BulkImportModal.jsx
import { useEffect, useRef, useState } from "react";
import { X, UploadCloud, Download, FileCheck } from "lucide-react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";

// Apply template-driven overrides to sampleRow, by matching header names (case-insensitive)
function applyTemplateOverrides(headers, sampleRowRaw, overrides) {
  const out = Array.isArray(sampleRowRaw) ? [...sampleRowRaw] : [];
  const ov = overrides && typeof overrides === "object" ? overrides : {};

  for (const [headerName, value] of Object.entries(ov)) {
    const idx = headers.findIndex(
      (h) =>
        String(h || "").trim().toLowerCase() ===
        String(headerName || "").trim().toLowerCase()
    );
    if (idx >= 0) out[idx] = value;
  }

  return out;
}

export default function BulkImportModal({
  open,
  title = "Import",
  subtitle = "Upload file Excel/CSV sesuai template.",
  accept = ".csv,.xlsx,.xls",
  template = {
    filename: "template.xlsx",
    headers: [],
    sampleRow: [],
    // optional:
    // overrides: { Role: "generalUser" }
  },
  uploading = false,
  onClose,
  onUpload,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);

  useEffect(() => {
    if (!open) {
      setDragOver(false);
      setPickedFile(null);
    }
  }, [open]);

  if (!open) return null;

  const pickFile = (file) => {
    if (!file) return;
    setPickedFile(file);
  };

  const handleBrowse = () => inputRef.current?.click();

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    pickFile(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    pickFile(file);
  };

  const handleDownloadTemplate = async () => {
    try {
      const headers = Array.isArray(template?.headers) ? template.headers : [];
      const sampleRowRaw = Array.isArray(template?.sampleRow) ? template.sampleRow : [];

      // ✅ generic override system (works for both Users + Assessments)
      // Example usage from parent:
      // template={{ ..., overrides: { Role: "generalUser" } }}
      const sampleRow = applyTemplateOverrides(headers, sampleRowRaw, template?.overrides);

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Template");

      // Plain default Excel look
      ws.addRow(headers);
      ws.addRow(sampleRow);

      // Optional column width (still plain)
      ws.columns = headers.map((h, i) => {
        const maxLen = Math.max(
          String(h || "").length,
          String(sampleRow?.[i] ?? "").length
        );
        return { width: Math.min(Math.max(maxLen + 2, 10), 40) };
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = (template?.filename || "template.xlsx")
        .replace(/\.csv$/i, ".xlsx")
        .replace(/\.xls$/i, ".xlsx");

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Gagal membuat template Excel.");
    }
  };

  const handleUploadClick = async () => {
    if (!pickedFile) {
      toast.info("Pilih file terlebih dahulu.");
      return;
    }
    await onUpload(pickedFile);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !uploading && onClose?.()}
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
          <div className="flex items-start justify-between p-6">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h3>
              <p className="text-sm text-slate-600 mt-1.5">{subtitle}</p>
            </div>

            <button
              type="button"
              disabled={uploading}
              onClick={() => onClose?.()}
              className="p-2 rounded-lg hover:bg-slate-200/60 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed group"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-slate-600 group-hover:text-slate-900 transition-colors" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Top action row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={uploading}
                onClick={handleBrowse}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Browse
              </button>

              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading || !pickedFile}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {uploading ? "Mengunggah..." : "Upload"}
              </button>

              <button
                type="button"
                onClick={() => !uploading && onClose?.()}
                disabled={uploading}
                className="px-4 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Batal
              </button>
            </div>

            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
          </div>

          {/* Hidden input */}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            className="hidden"
          />

          {/* Dropzone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={[
              "relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden",
              dragOver
                ? "border-blue-500 bg-blue-50/50 shadow-lg shadow-blue-100"
                : pickedFile
                ? "border-emerald-300 bg-emerald-50/30"
                : "border-slate-300 bg-slate-50/50 hover:border-slate-400 hover:bg-slate-100/50",
            ].join(" ")}
          >
            <div className="p-10">
              <div className="flex flex-col items-center text-center">
                <div
                  className={[
                    "rounded-full p-4 mb-4 transition-all duration-300",
                    dragOver
                      ? "bg-blue-100 scale-110"
                      : pickedFile
                      ? "bg-emerald-100"
                      : "bg-slate-200",
                  ].join(" ")}
                >
                  {pickedFile ? (
                    <FileCheck className="w-8 h-8 text-emerald-600" />
                  ) : (
                    <UploadCloud className="w-8 h-8 text-slate-600" />
                  )}
                </div>

                <h4 className="text-lg font-semibold text-slate-900 mb-2">
                  {pickedFile ? "File Terpilih" : "Drag & Drop File"}
                </h4>

                <p className="text-sm text-slate-600">
                  {pickedFile ? pickedFile.name : "Drop file di sini atau klik Browse untuk memilih file"}
                </p>

                <p className="text-xs text-slate-500 mt-4">
                  Format yang didukung: CSV, XLSX, XLS
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Tips: Anda bisa drag & drop file langsung ke area upload.
          </p>
        </div>
      </div>
    </div>
  );
}
